/**
 * Cloud-backed user preferences: dropdown lists + app settings.
 *
 * Both live in a single `user_prefs` row (jsonb columns: settings, lists).
 * Prefs.load() is awaited inside DB.init() so reads through Lists.get /
 * Settings.get are synchronous and consistent on first paint. Writes update
 * the in-memory cache + localStorage mirror immediately and debounce-push
 * to Supabase ~600ms later.
 */
import { sb, CLOUD_USER } from "./supabase";
import { toast } from "./dom";
import type { AppSettings } from "./types";

const LISTS_KEY = "enigma_lists_v1";
const SETTINGS_KEY = "enigma_settings_v1";

/* These keys + defaults match the legacy single-file app's DEFAULT_LISTS exactly.
   Don't rename keys — every view reads from one of these by name. New options can
   be added to a list freely; users can also customise via Configs → Dropdown Lists. */
export const DEFAULT_LISTS: Record<string, string[]> = {
  // One shared lifecycle list for BOTH cards and sealed (2026-07-09; replaces
  // the old "Card Status" + "Inventory Status" pair — user_prefs migrated).
  // "Opened" is sealed-only in practice (set by rips); the grading trio is
  // cards-only. Intent-style values (Long Hold, Flip/Hold, Rip Candidate)
  // were removed — intent lives elsewhere, Status is physical state only.
  "Status":           ["Available", "Listed", "At Grading", "Repair", "Sold", "Traded", "Opened", "Gifted", "Written Off"],
  "Acq Type":         ["Owner Contribution", "Purchase", "Trade", "Pull/Rip", "Grading Return"],
  "Txn Type":         ["Owner Contribution", "Purchase", "Sale", "Trade In", "Trade Out", "Rip/Open", "Grading", "Expense", "Adjustment"],
  "Product Type":     ["Booster Box", "Booster Bundle", "Booster Pack", "Sleeved Booster", "ETB", "Pokemon Center ETB", "Collection Box", "UPC/Premium", "Tin", "Blister", "Deck", "Case", "Other"],
  "Expense Category": ["Wages", "Contract labor", "Payroll taxes", "Grading", "Shipping", "Supplies", "Show Fees", "Mileage", "LLC Costs", "Software", "Insurance", "Storage", "Other"],
  "Sale Platform":    ["eBay", "TCGplayer", "WhatNot", "Local", "Card Show", "Other"],
  "Mileage Purpose":  ["Individual Purchase", "Card Show", "Post Office", "Supplies", "Grading Drop-Off", "Pickup", "Trade", "Other"],
};

export const DEFAULT_SETTINGS: AppSettings = {
  irsRate: 0.67,
  defaultEbayFeePct: 13.25,
  defaultShippingCost: 5,
  pokemonTcgApiKey: "",
  bulkUnitValue: 0.02,
  holoBulkUnitValue: 0.10,
  premiumBulkUnitValue: 0.50,
  bulk1to5UnitValue: 2,
  bulk5to10UnitValue: 6,
  theme: "dark",
  textSizeScale: "md",
  defaultLandingPage: "dashboard",
  lastFmvRefreshAt: "",
  startingCashBalance: 0,
  dashboardLayout: [],
};

class PrefsImpl {
  _lists: Record<string, string[]> | null = null;
  _settings: Partial<AppSettings> | null = null;
  _ready = false;
  private _pushTimer: ReturnType<typeof setTimeout> | null = null;

  /** Read from localStorage as a synchronous fallback. */
  private _fallback() {
    let lists: Record<string, string[]> | null = null;
    let settings: Partial<AppSettings> | null = null;
    try { lists = JSON.parse(localStorage.getItem(LISTS_KEY) || "null"); } catch { /* ignore */ }
    try { settings = JSON.parse(localStorage.getItem(SETTINGS_KEY) || "null"); } catch { /* ignore */ }
    this._lists = lists || JSON.parse(JSON.stringify(DEFAULT_LISTS));
    this._settings = { ...DEFAULT_SETTINGS, ...(settings || {}) };
  }

  /** Hydrate from cloud (or seed the row on first sign-in). */
  async load(): Promise<void> {
    this._fallback();
    if (!CLOUD_USER) { this._ready = true; return; }
    try {
      const { data, error } = await sb.from("user_prefs")
        .select("settings, lists")
        .eq("user_id", CLOUD_USER.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        const { error: insErr } = await sb.from("user_prefs").insert({
          user_id: CLOUD_USER.id,
          settings: this._settings,
          lists: this._lists,
        });
        if (insErr) console.error("[Prefs.load] seed insert:", insErr);
      } else {
        this._lists    = { ...DEFAULT_LISTS, ...((data.lists as Record<string, string[]>) || {}) };
        this._settings = { ...DEFAULT_SETTINGS, ...((data.settings as Partial<AppSettings>) || {}) };
        try { localStorage.setItem(LISTS_KEY, JSON.stringify(this._lists)); } catch { /* ignore */ }
        try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(this._settings)); } catch { /* ignore */ }
      }
    } catch (e) {
      console.error("[Prefs.load]", e);
    }
    this._ready = true;
  }

  /** Debounced push: coalesces rapid Settings.set / Lists.setAll calls. */
  schedulePush(): void {
    if (this._pushTimer) clearTimeout(this._pushTimer);
    this._pushTimer = setTimeout(() => { this._pushTimer = null; void this._pushNow(); }, 600);
  }

  private async _pushNow(): Promise<void> {
    if (!CLOUD_USER) return;
    const payload = {
      user_id: CLOUD_USER.id,
      settings: this._settings,
      lists: this._lists,
      updated_at: new Date().toISOString(),
    };
    try {
      const { error } = await sb.from("user_prefs").upsert(payload, { onConflict: "user_id" });
      if (error) throw error;
    } catch (e: any) {
      console.error("[Prefs._pushNow]", e);
      toast("Cloud sync (prefs) failed: " + (e?.message || e), "err");
    }
  }
}

export const Prefs = new PrefsImpl();

export const Lists = {
  get(name: string): string[] {
    const all = Prefs._lists || DEFAULT_LISTS;
    return all[name] || DEFAULT_LISTS[name] || [];
  },
  all(): Record<string, string[]> {
    return Prefs._lists || JSON.parse(JSON.stringify(DEFAULT_LISTS));
  },
  setAll(obj: Record<string, string[]>): void {
    Prefs._lists = obj || {};
    try { localStorage.setItem(LISTS_KEY, JSON.stringify(Prefs._lists)); } catch { /* ignore */ }
    Prefs.schedulePush();
  },
};

export const Settings = {
  get(): AppSettings {
    return { ...DEFAULT_SETTINGS, ...(Prefs._settings || {}) };
  },
  set(patch: Partial<AppSettings>): void {
    Prefs._settings = { ...(Prefs._settings || DEFAULT_SETTINGS), ...patch };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(Prefs._settings)); } catch { /* ignore */ }
    Prefs.schedulePush();
  },
};
